import type { Pinia } from 'pinia';
import { computed } from 'vue';

import { useAuthStore } from '@/stores/auth.store';
import { useOrganizationStore } from '@/stores/organzation.pinia';

/**
 * Single source of truth for the "restricted session" gates the app enforces
 * before a user is allowed into the product: email verification, phone
 * verification, and a pending (non-skipped) assessment. The router guard
 * (router/index.ts) redirects to the matching gate page, and the app bar uses
 * the same result to render the logo-only header and hide the action icons.
 *
 * This is a plain function (not a `use*` hook) so the router guard — which runs
 * outside any component setup — can call it too; pass the pinia instance there.
 * The reactive `useRestrictedSession()` below wraps it for components.
 *
 * Note: `assessmentGate` reads `organizationAssessments` from the org store, so
 * the router awaits `getOrganizationAssessment()` before calling this.
 */
export function getRestrictedSession(pinia?: Pinia) {
  const authStore = useAuthStore(pinia);
  const orgStore = useOrganizationStore(pinia);
  const user = authStore.user;

  const emailGate = !!user && user.isVerified === false;

  const phoneVerified = user?.phoneVerifications?.find(
    (v) => v.normalizedPhoneNumber === user?.normalizedPhoneNumber
  )?.isVerified;
  const phoneGate = !!user && !!orgStore.msegatEnabled && !phoneVerified;

  // Returns null when the user has no assessment access; `assessmentId` is the
  // first unanswered assessment (null when all are done).
  const assessment = user ? authStore.checkUserCompleteAssessment() : null;
  const assessmentId = assessment?.assessmentId ?? null;
  const assessmentGate = !!user && !user.skippedAssessmentId && !!assessmentId;

  return {
    emailGate,
    phoneGate,
    assessmentGate,
    isRestricted: emailGate || phoneGate || assessmentGate,
    // Extra detail the router needs to choose the assessment redirect target.
    assessmentId,
    hasStartedAnyAssessment: assessment?.hasStartedAnyAssessment ?? false,
  };
}

/**
 * Reactive wrapper for components (e.g. the app bar). Each getter re-evaluates
 * when the underlying auth/org state changes.
 */
export function useRestrictedSession() {
  const session = computed(() => getRestrictedSession());
  return {
    emailGate: computed(() => session.value.emailGate),
    phoneGate: computed(() => session.value.phoneGate),
    assessmentGate: computed(() => session.value.assessmentGate),
    isRestricted: computed(() => session.value.isRestricted),
  };
}
